import axios from 'axios';
import { guardarSesion, limpiarSesion, obtenerRefreshToken, obtenerToken } from './sesion';

axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URI;

// Crear una instancia de axios
const axiosInstance = axios.create();

// En estos endpoints un 401 significa credenciales inválidas, no sesión expirada:
// no se intenta refrescar ni se redirige.
const RUTAS_AUTENTICACION = [
  '/Usuario/Login',
  '/Usuario/ExternalLogin',
  '/Usuario/RefrescarToken',
  '/Usuario/CerrarSesion',
  '/SuscriptorAuth/',
];

const esRutaAutenticacion = (url = '') => RUTAS_AUTENTICACION.some((ruta) => url.includes(ruta));

const leerRolDesdeToken = () => {
  try {
    const token = obtenerToken();
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload["rol"] || null;
  } catch {
    return null;
  }
};

// Decide a qué login redirigir según el rol.
const loginUrlPorRol = (extraQuery = "") => {
  const rol = leerRolDesdeToken();
  const base = rol === "Suscriptor" ? "/suscriptor/login" : "/login";
  return base + extraQuery;
};

// Un solo refresh en vuelo: las demás peticiones con 401 esperan ese mismo resultado.
let refrescoEnCurso = null;

const refrescarSesion = () => {
  if (!refrescoEnCurso) {
    const refreshToken = obtenerRefreshToken();
    if (!refreshToken) return Promise.resolve(null);

    refrescoEnCurso = axios
      .post('/Usuario/RefrescarToken', { refreshToken },
        { headers: { 'Content-Type': 'application/json' } })
      .then((response) => guardarSesion(response.data))
      .catch(() => null)
      .finally(() => { refrescoEnCurso = null; });
  }
  return refrescoEnCurso;
};

// Agregar un interceptor para agregar el token a cada solicitud
axiosInstance.interceptors.request.use(
  (config) => {
    const token = obtenerToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Agregar un interceptor para manejar errores
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status !== 401 || !original || esRutaAutenticacion(original.url || '')) {
      return Promise.reject(error);
    }

    // El access token dura 15 minutos: se intenta renovarlo una sola vez por petición.
    if (!original._reintentado) {
      original._reintentado = true;
      const nuevoToken = await refrescarSesion();
      if (nuevoToken) {
        // El interceptor de request vuelve a poner el header con el token nuevo.
        return axiosInstance(original);
      }
    }

    // Sesión no recuperable. Calcular el destino ANTES de borrar el token (necesitamos el rol).
    const deshabilitado = error.response.data === "Usuario deshabilitado.";
    const destino = deshabilitado ? loginUrlPorRol("?error=disabled") : loginUrlPorRol();

    limpiarSesion();
    window.location.href = destino;
    return Promise.reject(error);
  }
);

// Función para validar el estado del usuario
export const validarEstadoUsuario = async () => {
  try {
    await axiosInstance.get('/Usuario/ValidateUserStatus');
    return true;
  } catch (error) {
    // El interceptor ya maneja los errores de autorización
    return false;
  }
};

// Cierra la sesión en el backend (revoca el refresh token) y limpia el almacenamiento local.
export const cerrarSesion = async () => {
  const refreshToken = obtenerRefreshToken();
  if (refreshToken) {
    try {
      await axiosInstance.post('/Usuario/CerrarSesion', { refreshToken });
    } catch {
      // Si falla la revocación igual se limpia la sesión local.
    }
  }
  limpiarSesion();
};

const requestGenerico = {
    get: (url, config = undefined) => axiosInstance.get(url, config),
    post: (url, body, config = undefined) => axiosInstance.post(url, body, config),
    put: (url, body, config = undefined) => axiosInstance.put(url, body, config),
    delete: (url, config = undefined) => axiosInstance.delete(url, config),
    validarEstadoUsuario
};

export default requestGenerico;
